/**
 * Abstract Technology - non-representational motion for technology episodes.
 * Flowing data lines, drifting particles and a pulsing perspective grid.
 * No letters, no symbols that read as text (spec: no on-screen text).
 */

import { createRng, drawGrain, drawVignette, glow, reactive, withAlpha } from './shared.js';

const WORLD = (() => {
  const rng = createRng(0x1de4a);

  const lines = Array.from({ length: 22 }, () => ({
    ny: rng(),
    cycles: 1 + Math.floor(rng() * 3),
    offset: rng(),
    length: 0.12 + rng() * 0.5,
    thickness: 0.0012 + rng() * 0.004,
    warm: rng(),
    alpha: 0.15 + rng() * 0.5,
    bend: (rng() - 0.5) * 0.06,
  }));

  const particles = Array.from({ length: 90 }, () => ({
    nx: rng(),
    ny: rng(),
    cycles: 1 + Math.floor(rng() * 2),
    offset: rng(),
    radius: 0.0009 + rng() * 0.0026,
    alpha: 0.1 + rng() * 0.45,
    drift: 0.01 + rng() * 0.04,
  }));

  return { lines, particles };
})();

export default {
  id: 'abstract-tech',
  label: 'Abstract Technology',
  description: 'Procedural data lines, particles and a pulsing perspective grid.',
  loopable: true,

  draw(ctx, { t, loopSeconds, w, h, audio, strength }) {
    const phase = (t % loopSeconds) / loopSeconds;
    const level = reactive(audio.level, strength);
    const bass = reactive(audio.bass, strength);
    const treble = reactive(audio.treble, strength);

    // --- base field
    const base = ctx.createLinearGradient(0, 0, w, h);
    base.addColorStop(0, '#05070C');
    base.addColorStop(0.5, '#080C14');
    base.addColorStop(1, '#04060A');
    ctx.fillStyle = base;
    ctx.fillRect(0, 0, w, h);

    // --- perspective grid with a slow breathing pulse
    const horizon = h * 0.42;
    const pulse = 0.5 + 0.5 * Math.sin(phase * Math.PI * 2);
    ctx.save();
    ctx.strokeStyle = withAlpha('#3D7FB8', 0.1 + pulse * 0.08 + bass * 0.1);
    ctx.lineWidth = Math.max(1, w * 0.0015);

    // horizontal lines receding toward the horizon
    for (let i = 0; i < 16; i += 1) {
      const k = (i + (phase * 2) % 1) / 16;
      const y = horizon + Math.pow(k, 2.4) * (h - horizon) * 1.25;
      if (y > h) continue;
      ctx.globalAlpha = 0.35 + 0.65 * k;
      ctx.beginPath();
      ctx.moveTo(0, y);
      ctx.lineTo(w, y);
      ctx.stroke();
    }

    // vertical lines converging on a vanishing point
    const vpX = w / 2;
    ctx.globalAlpha = 1;
    for (let i = -8; i <= 8; i += 1) {
      const spread = i * w * 0.14;
      ctx.beginPath();
      ctx.moveTo(vpX + spread * 0.12, horizon);
      ctx.lineTo(vpX + spread, h);
      ctx.stroke();
    }
    ctx.restore();

    // --- horizon glow
    glow(ctx, vpX, horizon, w * 0.75, '#2B6FA8', 0.14 + level * 0.14);

    // --- flowing data lines
    ctx.lineCap = 'round';
    for (const l of WORLD.lines) {
      const travel = (l.offset + phase * l.cycles) % 1.15;
      const x = (1.15 - travel) * w * 1.15 - w * 0.15;
      const y = l.ny * h;
      const len = l.length * w;
      const color = l.warm > 0.7 ? '#FF9A4D' : l.warm > 0.4 ? '#7FD6FF' : '#B8C6FF';
      const gradient = ctx.createLinearGradient(x, 0, x + len, 0);
      gradient.addColorStop(0, withAlpha(color, 0));
      gradient.addColorStop(0.35, withAlpha(color, l.alpha * (0.75 + level * 0.5)));
      gradient.addColorStop(1, withAlpha(color, 0));
      ctx.strokeStyle = gradient;
      ctx.lineWidth = Math.max(1, l.thickness * w);
      ctx.beginPath();
      ctx.moveTo(x, y);
      ctx.quadraticCurveTo(x + len * 0.5, y + l.bend * h, x + len, y);
      ctx.stroke();
    }

    // --- drifting particles
    for (const p of WORLD.particles) {
      const rise = (p.offset + phase * p.cycles) % 1;
      const y = (p.ny - rise * 0.3 + 1) % 1;
      const x = p.nx + Math.sin((phase + p.offset) * Math.PI * 2) * p.drift;
      const flicker = 0.6 + 0.4 * Math.sin((phase + p.offset) * Math.PI * 2 * 2);
      ctx.fillStyle = withAlpha('#CFE4FF', p.alpha * flicker * (0.7 + treble * 0.6));
      ctx.beginPath();
      ctx.arc(x * w, y * h, Math.max(0.6, p.radius * w), 0, Math.PI * 2);
      ctx.fill();
    }

    // --- soft accent sweep, keeps the frame from feeling static
    const sweep = phase < 0.5 ? phase * 2 : (1 - phase) * 2;
    const sweepY = sweep * h;
    const sweepGradient = ctx.createLinearGradient(0, sweepY - h * 0.12, 0, sweepY + h * 0.12);
    sweepGradient.addColorStop(0, withAlpha('#FF7A00', 0));
    sweepGradient.addColorStop(0.5, withAlpha('#FF7A00', 0.035 + level * 0.03));
    sweepGradient.addColorStop(1, withAlpha('#FF7A00', 0));
    ctx.fillStyle = sweepGradient;
    ctx.fillRect(0, sweepY - h * 0.12, w, h * 0.24);

    drawVignette(ctx, w, h, 0.55);
    drawGrain(ctx, w, h, { t, alpha: 0.025 });
  },
};
